import { BLOCK_TYPES } from './BlockTypes.js'

export class DungeonGrid {
  constructor(cols, rows, cellSize, originX, originY, terrainType, areaId = terrainType) {
    this.cols = cols
    this.rows = rows
    this.cellSize = cellSize
    this.originX = originX
    this.originY = originY
    this.terrainType = terrainType
    this.areaId = areaId

    // 2D array of cells [col][row]
    this.cells = []
    for (let c = 0; c < cols; c++) {
      this.cells[c] = []
      for (let r = 0; r < rows; r++) {
        this.cells[c][r] = {
          visible: false,
          walls: { n: true, s: true, e: true, w: true },
          hasKey: false,
          lockedWalls: { n: false, s: false, e: false, w: false },
        }
      }
    }
  }

  getCell(col, row) {
    if (col < 0 || col >= this.cols || row < 0 || row >= this.rows) return null
    return this.cells[col][row]
  }

  setVisible(col, row) {
    const cell = this.getCell(col, row)
    if (cell) cell.visible = true
  }

  connect(col1, row1, col2, row2) {
    const a = this.getCell(col1, row1)
    const b = this.getCell(col2, row2)
    if (!a || !b) return

    a.visible = true
    b.visible = true

    const dc = col2 - col1
    const dr = row2 - row1

    if (dc === 1 && dr === 0) { a.walls.e = false; b.walls.w = false }
    if (dc === -1 && dr === 0) { a.walls.w = false; b.walls.e = false }
    if (dr === 1 && dc === 0) { a.walls.s = false; b.walls.n = false }
    if (dr === -1 && dc === 0) { a.walls.n = false; b.walls.s = false }
  }

  placeKey(col, row) {
    const cell = this.getCell(col, row)
    if (cell) cell.hasKey = true
  }

  placeLock(col1, row1, col2, row2) {
    // Place a locked door on the wall between two cells.
    // Both cells must already be connected. We mark BOTH sides as locked
    // so the stamp fills both wall rows with wall + locked door.
    const dc = col2 - col1
    const dr = row2 - row1
    const a = this.getCell(col1, row1)
    const b = this.getCell(col2, row2)
    if (!a || !b) return

    // The shared wall between A and B: for a south connection, B's north wall
    // IS the shared wall. Mark the appropriate side as locked on both cells
    // so the shared wall stays solid with a locked door in it.
    if (dc === 1)  { a.lockedWalls.e = true; b.lockedWalls.w = true }
    if (dc === -1) { a.lockedWalls.w = true; b.lockedWalls.e = true }
    if (dr === 1)  { a.lockedWalls.s = true; b.lockedWalls.n = true }
    if (dr === -1) { a.lockedWalls.n = true; b.lockedWalls.s = true }
  }

  // Open the south wall of the bottom-center cell for phase transition.
  openSouthExit(col) {
    const cell = this.getCell(col, this.rows - 1)
    if (cell) cell.walls.s = false
  }

  // Open the north wall of the top-center cell for phase transition.
  openNorthEntrance(col) {
    const cell = this.getCell(col, 0)
    if (cell) cell.walls.n = false
  }

  // Stamp tiles into the world tile map.
  stampToTiles(tiles, protectedTiles, getFloorTile, wallTile = BLOCK_TYPES.WALL) {
    const cs = this.cellSize
    // Walls are shared between adjacent cells. Each cell owns its top and left
    // wall lines. The bottom/right walls belong to the next cell (or the grid edge).
    // Room interior = everything except the top row and left column.
    const doorHalf = 0 // 0 = single tile doorway

    for (let c = 0; c < this.cols; c++) {
      for (let r = 0; r < this.rows; r++) {
        const cell = this.cells[c][r]
        const cx = this.originX + c * cs
        const cy = this.originY + r * cs

        if (!cell.visible) continue

        // Each cell owns its TOP row (cy) and LEFT column (cx).
        // Room interior starts at (cx+1, cy+1) and goes to (cx+cs-1, cy+cs-1).
        // The bottom row (cy+cs) belongs to the next row of cells (or grid edge).
        // The right column (cx+cs) belongs to the next column (or grid edge).

        // Top wall row
        for (let x = cx; x < cx + cs; x++) {
          tiles.set(`${x},${cy}`, wallTile)
        }

        // Left wall column
        for (let y = cy; y < cy + cs; y++) {
          tiles.set(`${cx},${y}`, wallTile)
        }

        // Right wall column: draw if last column OR if east neighbor is not visible
        const eastNeighbor = this.getCell(c + 1, r)
        if (c === this.cols - 1 || !eastNeighbor || !eastNeighbor.visible) {
          for (let y = cy; y <= cy + cs; y++) {
            tiles.set(`${cx + cs},${y}`, wallTile)
          }
        }

        // Bottom wall row: draw if last row OR if south neighbor is not visible
        const southNeighbor = this.getCell(c, r + 1)
        if (r === this.rows - 1 || !southNeighbor || !southNeighbor.visible) {
          for (let x = cx; x <= cx + cs; x++) {
            tiles.set(`${x},${cy + cs}`, wallTile)
          }
        }

        // Fill room interior with floor
        for (let x = cx + 1; x < cx + cs; x++) {
          for (let y = cy + 1; y < cy + cs; y++) {
            const key = `${x},${y}`
            tiles.set(key, getFloorTile(x, y))
            protectedTiles.add(key)
          }
        }

        const roomCenterX = cx + Math.floor(cs / 2)
        const roomCenterY = cy + Math.floor(cs / 2)

        // Carve door openings (single tile) through shared walls.
        // North wall = top row of this cell (cy)
        if (!cell.walls.n && !cell.lockedWalls.n) {
          for (let x = roomCenterX - doorHalf; x <= roomCenterX + doorHalf; x++) {
            const key = `${x},${cy}`
            tiles.set(key, getFloorTile(x, cy))
            protectedTiles.add(key)
          }
        }

        // South wall = top row of next cell (cy + cs), only carve if last row
        if (!cell.walls.s && !cell.lockedWalls.s && r === this.rows - 1) {
          for (let x = roomCenterX - doorHalf; x <= roomCenterX + doorHalf; x++) {
            const key = `${x},${cy + cs}`
            tiles.set(key, getFloorTile(x, cy + cs))
            protectedTiles.add(key)
          }
        }

        // West wall = left column of this cell (cx)
        if (!cell.walls.w && !cell.lockedWalls.w) {
          for (let y = roomCenterY - doorHalf; y <= roomCenterY + doorHalf; y++) {
            const key = `${cx},${y}`
            tiles.set(key, getFloorTile(cx, y))
            protectedTiles.add(key)
          }
        }

        // East wall = left column of next cell (cx + cs), only carve if last col
        if (!cell.walls.e && !cell.lockedWalls.e && c === this.cols - 1) {
          for (let y = roomCenterY - doorHalf; y <= roomCenterY + doorHalf; y++) {
            const key = `${cx + cs},${y}`
            tiles.set(key, getFloorTile(cx + cs, y))
            protectedTiles.add(key)
          }
        }

        // For non-edge cells, the south/east openings are carved by the
        // neighbor's north/west opening logic. But we need to handle the case
        // where the neighbor is not visible. So also carve from this side:
        if (!cell.walls.s && !cell.lockedWalls.s && r < this.rows - 1) {
          // South opening = next cell's north wall (cy + cs)
          const nextCell = this.getCell(c, r + 1)
          if (!nextCell || !nextCell.visible) {
            for (let x = roomCenterX - doorHalf; x <= roomCenterX + doorHalf; x++) {
              const key = `${x},${cy + cs}`
              tiles.set(key, getFloorTile(x, cy + cs))
              protectedTiles.add(key)
            }
          }
          // The neighbor handles it via its north wall opening
        }

        if (!cell.walls.e && !cell.lockedWalls.e && c < this.cols - 1) {
          const nextCell = this.getCell(c + 1, r)
          if (!nextCell || !nextCell.visible) {
            for (let y = roomCenterY - doorHalf; y <= roomCenterY + doorHalf; y++) {
              const key = `${cx + cs},${y}`
              tiles.set(key, getFloorTile(cx + cs, y))
              protectedTiles.add(key)
            }
          }
        }

        // Stamp locked doors on shared wall.
        if (cell.lockedWalls.n) {
          const k = `${roomCenterX},${cy}`
          tiles.set(k, BLOCK_TYPES.LOCKED_DOOR)
          protectedTiles.add(k)
        }
        if (cell.lockedWalls.s) {
          const wallY = r === this.rows - 1 ? cy + cs : cy + cs
          const k = `${roomCenterX},${wallY}`
          tiles.set(k, BLOCK_TYPES.LOCKED_DOOR)
          protectedTiles.add(k)
        }
        if (cell.lockedWalls.w) {
          const k = `${cx},${roomCenterY}`
          tiles.set(k, BLOCK_TYPES.LOCKED_DOOR)
          protectedTiles.add(k)
        }
        if (cell.lockedWalls.e) {
          const wallX = c === this.cols - 1 ? cx + cs : cx + cs
          const k = `${wallX},${roomCenterY}`
          tiles.set(k, BLOCK_TYPES.LOCKED_DOOR)
          protectedTiles.add(k)
        }
      }
    }
  }

  // Get world-space center of a cell (for spawn points etc).
  getCellCenter(col, row) {
    const cx = this.originX + col * this.cellSize + Math.floor(this.cellSize / 2)
    const cy = this.originY + row * this.cellSize + Math.floor(this.cellSize / 2)
    return { x: cx, y: cy }
  }

  // Get world-space bounds of the entire grid.
  getBounds() {
    return {
      minX: this.originX,
      maxX: this.originX + this.cols * this.cellSize,
      minY: this.originY,
      maxY: this.originY + this.rows * this.cellSize,
    }
  }
}
